import { designTokens } from "@itandi/itandi-bb-ui";
import { Box } from "@itandi/itandi-bb-ui/Box";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormControlText } from "@itandi/itandi-bb-ui/FormControlText";
import { Grid } from "@itandi/itandi-bb-ui/Grid";
import { Input } from "@itandi/itandi-bb-ui/Input";
import { InputRadio } from "@itandi/itandi-bb-ui/InputRadio";
import { InputText } from "@itandi/itandi-bb-ui/InputText";
import { SingleComboBox } from "@itandi/itandi-bb-ui/SingleComboBox";
import { BigNumber } from "bignumber.js";
import type { ComponentProps } from "react";
import React, { useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { MANUAL_INPUT_CONSTRUCTION_ID } from "~/constants/DealConstructionConst";
import { MEDIA_QUERY, useMediaQuery } from "~/hooks/useMediaQuery";
import type { DealConstructionEdit } from "~/models/DealConstruction";
import { ESTIMATION_TYPE } from "~/models/DealConstruction";
import type { DealConstructionItem } from "~/models/DealConstructionItem";
import Styles from "./ConstructionForm.module.scss";

type Props = {
	dealConstructionItems: DealConstructionItem[];
	disabled: boolean;
	docType: "estimation" | "billing";
};

const MANUAL_INPUT_PREFIX = "manual-input-";

const normalizeString = (str: string): string =>
	str.normalize("NFKC").toLowerCase().trim();

export const ConstructionForm: React.FC<Props> = ({
	dealConstructionItems,
	disabled,
	docType
}: Props) => {
	const { register, formState, setValue, watch } =
		useFormContext<DealConstructionEdit>();
	const { errors } = formState;
	const dealConstructionItem = watch("dealConstructionItem");
	const companyChargePer = watch("companyChargePer");
	const unit = watch("unit");
	const unitAmount = watch("unitAmount");

	const { match: isPc } = useMediaQuery(MEDIA_QUERY.pc);

	const getOptions = useCallback(
		(inputValue: string) => {
			const options: ComponentProps<typeof SingleComboBox>["options"] =
				dealConstructionItems.map((item: DealConstructionItem) => ({
					label: item.name,
					value: String(item.id)
				}));

			// 入力テキストがあり、既存の選択肢に一致しない場合は「直接入力」オプションを追加
			if (inputValue.trim() !== "") {
				const hasExactMatch = dealConstructionItems.some(
					(item) => normalizeString(item.name) === normalizeString(inputValue)
				);
				if (!hasExactMatch) {
					options.push({
						label: `「${inputValue}」を使用`,
						value: `${MANUAL_INPUT_PREFIX}${inputValue}`
					});
				}
			}

			return options;
		},
		[dealConstructionItems]
	);

	const [constructionItemOptions, setConstructionItemOptions] = React.useState<
		ComponentProps<typeof SingleComboBox>["options"]
	>(() => getOptions(""));

	const selectedConstructionItemValue = React.useMemo(() => {
		if (!dealConstructionItem) {
			// 未選択
			return null;
		}

		// 直接入力の場合
		if (dealConstructionItem.id === MANUAL_INPUT_CONSTRUCTION_ID) {
			return {
				label: dealConstructionItem.name,
				value: `${MANUAL_INPUT_PREFIX}${dealConstructionItem.name}`
			};
		}

		return {
			label: dealConstructionItem.name,
			value: String(dealConstructionItem.id)
		};
	}, [dealConstructionItem]);

	const onInputChangeConstructionItem = useCallback(
		(text: string): void => {
			setConstructionItemOptions(getOptions(text));
		},
		[getOptions]
	);

	const onChangeConstructionItem = useCallback(
		(option: ComponentProps<typeof SingleComboBox>["value"]): void => {
			// 選択状態を解除した場合
			if (!option) {
				setValue("dealConstructionItem", null);
				setValue("unitAmount", 0);
				setConstructionItemOptions(getOptions(""));
				return;
			}

			// 「直接入力」オプションが選択された場合
			// dealConstructionItemには特殊なIDを使用して保存
			if (option.value.startsWith(MANUAL_INPUT_PREFIX)) {
				const customName = String(option.value).slice(
					MANUAL_INPUT_PREFIX.length
				);
				setValue("dealConstructionItem", {
					constructionItemId: MANUAL_INPUT_CONSTRUCTION_ID,
					excludeEstimation: true,
					id: MANUAL_INPUT_CONSTRUCTION_ID,
					name: customName,
					overridable: false,
					seq: 0,
					unit: "",
					unitAmount: 0
				});
				setValue("unitAmount", 0);
				setConstructionItemOptions(getOptions(""));
				return;
			}

			// 元々選択肢として存在している施工項目が選択された場合
			const findItem =
				dealConstructionItems.find(
					(item: DealConstructionItem) => item.id === Number(option.value)
				) ?? null;
			setValue("dealConstructionItem", findItem);
			setValue("unitAmount", findItem?.unitAmount ?? 0);
			if (findItem?.excludeEstimation === false) {
				setValue("estimationType", ESTIMATION_TYPE.NONE);
			}
			setConstructionItemOptions(getOptions(""));
		},
		[dealConstructionItems, setValue, getOptions]
	);

	const reCalcTotalAmount = useCallback(
		(quantity: number, price: number): void => {
			if (Number.isNaN(quantity) || Number.isNaN(price)) {
				return;
			}
			const newTotalAmount: number = BigNumber(quantity)
				.multipliedBy(BigNumber(price))
				.toNumber();
			setValue("totalAmount", newTotalAmount);
		},
		[setValue]
	);
	React.useEffect(() => {
		reCalcTotalAmount(unit, unitAmount);
	}, [reCalcTotalAmount, unit, unitAmount]);

	const handleChangeRenteeChargePer = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>): void => {
			const { value } = e.target;
			if (Number.isNaN(Number(value))) return;
			setValue("renteeChargePer", value === "" ? "" : Number(value));
			if (Number(value) > 100) return;
			const ownerChargePer = 100 - Number(value) - Number(companyChargePer);
			setValue("ownerChargePer", ownerChargePer);
		},
		[companyChargePer, setValue]
	);
	const estimationType = watch("estimationType");
	const isProposalDisabled = dealConstructionItem?.excludeEstimation === false;
	return (
		<div>
			<Box margin="8px">
				<Flex direction="column" gap="16px">
					<FormControl chip="必須" label="施工項目" variant="vertical">
						<SingleComboBox
							disabled={disabled}
							isInvalid={!!errors.dealConstructionItem}
							keepMenuOpenOnBlur
							maxMenuHeight={isPc ? "95dvh" : 300}
							menuPosition={isPc ? "right" : "bottom"}
							onChange={onChangeConstructionItem}
							onInputChange={onInputChangeConstructionItem}
							options={constructionItemOptions}
							placeholder="施工項目を検索"
							value={selectedConstructionItemValue}
							w="100%"
						/>
						{errors.dealConstructionItem ? (
							<div className={Styles.InvalidFeedback}>
								{errors.dealConstructionItem.message}
							</div>
						) : null}
						<FormControlText help="施工項目を検索できます。項目が存在しない場合は入力内容を使用できます。" />
					</FormControl>
					{docType === "estimation" ? (
						<FormControl label="提案ラベル" variant="vertical">
							<Flex gap={designTokens["spacing/s"]}>
								<InputRadio
									checked={estimationType === ESTIMATION_TYPE.NONE}
									disabled={disabled}
									label="なし"
									onChange={(): void => {
										setValue("estimationType", ESTIMATION_TYPE.NONE);
									}}
									value={ESTIMATION_TYPE.NONE}
								/>
								<InputRadio
									checked={estimationType === ESTIMATION_TYPE.PROPOSAL}
									disabled={disabled || isProposalDisabled}
									label="あり"
									onChange={(): void => {
										setValue("estimationType", ESTIMATION_TYPE.PROPOSAL);
									}}
									value={ESTIMATION_TYPE.PROPOSAL}
								/>
							</Flex>
						</FormControl>
					) : null}
					<FormControl label="施工箇所" variant="vertical">
						<Input
							disabled={disabled}
							isInvalid={!!errors.point}
							placeholder="例）リビング"
							{...register("point")}
						/>
					</FormControl>
					<Flex gap="8px">
						<Grid width="50%">
							<FormControl chip="必須" label="単価" variant="vertical">
								<Input
									disabled={disabled}
									isInvalid={!!errors.unitAmount}
									placeholder="単価"
									type="number"
									{...register("unitAmount")}
								/>
							</FormControl>
						</Grid>
						<Grid width="50%">
							<FormControl chip="必須" label="数量" variant="vertical">
								<Input
									disabled={disabled}
									isInvalid={!!errors.unit}
									placeholder="数量"
									type="number"
									{...register("unit")}
								/>
							</FormControl>
						</Grid>
					</Flex>
					<Flex gap="8px">
						<Grid width="50%">
							{errors.unitAmount ? (
								<div className={Styles.InvalidFeedback}>
									{errors.unitAmount.message}
								</div>
							) : null}
						</Grid>
						<Grid width="50%">
							{errors.unit ? (
								<div className={Styles.InvalidFeedback}>
									{errors.unit.message}
								</div>
							) : null}
						</Grid>
					</Flex>
					<Flex gap="8px">
						<Grid width="33%">
							<FormControl label="借主負担%" variant="vertical">
								<Input
									disabled={disabled}
									isInvalid={!!errors.renteeChargePer}
									placeholder="借主負担％"
									{...register("renteeChargePer")}
									onChange={handleChangeRenteeChargePer}
								/>
							</FormControl>
						</Grid>
						<Grid width="33%">
							<FormControl label="貸主負担%" variant="vertical">
								<Input
									disabled={disabled}
									isInvalid={!!errors.ownerChargePer}
									placeholder="貸主負担％"
									{...register("ownerChargePer")}
								/>
							</FormControl>
						</Grid>
						<Grid width="33%">
							<FormControl label="管理負担%" variant="vertical">
								<Input
									disabled={disabled}
									isInvalid={!!errors.companyChargePer}
									placeholder="管理負担％"
									{...register("companyChargePer")}
								/>
							</FormControl>
						</Grid>
					</Flex>
					{errors.renteeChargePer ||
					errors.ownerChargePer ||
					errors.companyChargePer ? (
						<div className={Styles.InvalidFeedback}>
							{errors.renteeChargePer?.message ??
								errors.ownerChargePer?.message ??
								errors.companyChargePer?.message}
						</div>
					) : null}
					<FormControl label="備考" variant="vertical">
						<InputText
							disabled={disabled}
							isInvalid={!!errors.note}
							maxRows={8}
							placeholder="備考）その他連絡事項など"
							resize="autoRows"
							rows={2}
							{...register("note")}
						/>
					</FormControl>
				</Flex>
			</Box>
		</div>
	);
};
